import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KpiDropdownActions,
  KpiType,
  StorePerformanceInterval,
  createStorePerformanceRequest,
} from '../../../../../constants';
import {
  SCOShareOfReceiptsItem,
  useKpiTargetDistribution,
  useStorePerformance,
  useStores,
  useTransaction,
  useUser,
} from '../../../../../hooks';
import { Container, Icon, Icons, Typography } from '../../../../../stories/atoms';
import { DropdownMenu } from '../../../../../stories/molecules';
import { muiTheme } from '../../../../../theme';
import { homeTranslations, kpiTargetTranslations } from '../../../../../translations';
import {
  formatAmountWithSeparator,
  getKpiTargetProps,
  getKpiTargetValue,
  isFailureResponse,
} from '../../../../../utils';
import { WidgetMetricTile, WidgetMetricType } from '../../../common/metricTile/WidgetMetricTile';
import { WidgetMetricTilesContainer } from '../../../common/metricTile/WidgetMetricTile.styles';
import { getDefaultStatus } from '../../../common/metricTile/util';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import WidgetChart from '../../../common/widgetChart/WidgetChart';
import { WidgetMobileLayout } from '../../../common/widgetMobileLayout/WidgetMobileLayout';
import { StyledView } from '../shareOfReceipts/ShareOfReceipts.styles';
import { isMultiDayInterval } from '../../../util';
import { useCelebrateTarget } from '../../../../../hooks/useCelebrateTarget';

export type SCOShareOfReceiptsProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  hasPermissionToEditKpi: boolean;
  transactionEventsCount: number;
  isToday: boolean;
  isKpiLoading: boolean;
  isKpiError: boolean;
  handleDropdownDetails: (kpiType: KpiType, action: KpiDropdownActions) => void;
  onTargetAchieved?: (targetValue: string, updatedDate: Date) => void;
};

export const SCOShareOfReceipts: FC<SCOShareOfReceiptsProps> = observer(
  ({
    interval,
    currentDate,
    hasPermissionToEditKpi,
    transactionEventsCount,
    isToday,
    isKpiLoading,
    isKpiError,
    handleDropdownDetails,
    onTargetAchieved,
  }) => {
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

    const {
      get: { currentStoreId, kpiTargetsProps },
    } = useUser();

    const { getKpiTargetDistribution } = useKpiTargetDistribution();
    const { fetchSCOShareOfReceiptsData } = useTransaction();
    const { fetchForecastForKpis } = useStores();

    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const {
      performanceItems,
      performanceItemsLoading,
      performanceItemsError,

      filteredPerformanceItems,

      forecastPerformanceItems,
      triggerGetStorePerformance,
    } = useStorePerformance<SCOShareOfReceiptsItem>();

    const isTargetLocked = getKpiTargetProps(kpiTargetsProps, 'dailySCOShareOfReceipts')?.isLocked ?? false;

    const { t } = useTranslation();
    const target = getKpiTargetValue(kpiTargetsProps, 'dailySCOShareOfReceipts');

    const {
      scoShareOfReceipts: scoShareOfReceiptsTranslations,
      common: commonTranslations,
      kpiTargets,
    } = homeTranslations;

    const itemValueResolver = (item: SCOShareOfReceiptsItem) => {
      const { totalSCOTransactions, totalTransactions } = item;
      return totalTransactions === 0 ? 0 : (totalSCOTransactions / totalTransactions) * 100;
    };

    const fetchData = useCallback(async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchSCOShareOfReceiptsData(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      setLastUpdated(response.data.lastUpdated);

      return response.data.shareOfReceiptsSCO;
    }, [interval, currentDate]);

    const fetchForecastData = async () => {
      const response = await fetchForecastForKpis<SCOShareOfReceiptsItem>('shareOfReceiptSCO');
      if (isFailureResponse(response)) {
        throw new Error();
      }
      return response.data;
    };

    const getShareOfReceipts = (items: (SCOShareOfReceiptsItem | null)[]) => {
      let currentTotalSCOTransactions = 0;
      let currentTotalTransactions = 0;

      items.forEach((item) => {
        if (item === null) return;

        const { totalSCOTransactions, totalTransactions } = item;

        if (totalTransactions === 0) return;

        currentTotalSCOTransactions += totalSCOTransactions;
        currentTotalTransactions += totalTransactions;
      });

      const currentShareOfReceipts =
        currentTotalTransactions === 0
          ? 0
          : Number.parseFloat(((currentTotalSCOTransactions / currentTotalTransactions) * 100).toFixed(2));

      return {
        currentShareOfReceipts: currentShareOfReceipts,
        currentTotalSCOTransactions: currentTotalSCOTransactions,
      };
    };

    const fetchWidgetData = () => {
      triggerGetStorePerformance({
        interval: interval,
        currentDate: currentDate,
        isToday: isToday,
        getDataAction: fetchData,
        getHasValue: (item) => item.totalTransactions !== 0,
        getForecastDataAction: fetchForecastData,
      });
    };

    useEffect(() => {
      fetchWidgetData();
    }, [currentDate, transactionEventsCount]);

    const targetItems = getKpiTargetDistribution({
      performanceItems: performanceItems,
      kpiName: 'dailySCOShareOfReceipts',
    });

    const showForecast = !!target && forecastPerformanceItems.some((item) => item !== null);

    const isEditKPIDisabled = performanceItemsError || !hasPermissionToEditKpi;

    const { currentShareOfReceipts, currentTotalSCOTransactions } = getShareOfReceipts(filteredPerformanceItems);

    const isNoData = currentShareOfReceipts === 0;

    const { currentShareOfReceipts: forecastShareOfReceipts } = getShareOfReceipts([
      ...filteredPerformanceItems,
      ...forecastPerformanceItems,
    ]);

    const targetProgressPercent = target && target != 0 ? (forecastShareOfReceipts / target) * 100 : 0;

    const isPreviousDay = isMultiDayInterval(interval) || !isToday;

    useCelebrateTarget({
      target,
      currentValue: forecastShareOfReceipts,
      isToday,
      localStorageKey: 'scoShareOfReceipts',
      lastUpdated: lastUpdated ?? currentDate,
      onTargetAchieved: (targetSet: number, updatedDate: Date) => {
        const formattedTarget = `${targetSet.toFixed(2)}%`;
        if (onTargetAchieved) {
          onTargetAchieved(formattedTarget, updatedDate);
        }
      },
    });

    const menuItems = [
      {
        key: target ? 'editKpi' : 'addKpi',
        disabled: isEditKPIDisabled || isTargetLocked || isPreviousDay,
        hasTooltip: isEditKPIDisabled || isTargetLocked || isPreviousDay,
        tooltipText: (
          <Container padding={[1]}>
            <Typography color="white">
              {!hasPermissionToEditKpi
                ? t(kpiTargets.noPermissionTooltip.key, kpiTargets.noPermissionTooltip.defaultValue)
                : isTargetLocked
                ? t(kpiTargets.lockedTargetTooltip.key, kpiTargets.lockedTargetTooltip.defaultValue)
                : isPreviousDay
                ? t(kpiTargets.previousDayTooltip.key, kpiTargets.previousDayTooltip.defaultValue)
                : t(kpiTargets.disabledTooltip.key, kpiTargets.disabledTooltip.defaultValue)}
            </Typography>
          </Container>
        ),
        item: (
          <Typography margin={[0, 2, 0]}>
            {target
              ? t(kpiTargets.editKpi.key, kpiTargets.editKpi.defaultValue)
              : t(kpiTargets.addKpi.key, kpiTargets.addKpi.defaultValue)}
          </Typography>
        ),
      },
    ];

    return (
      <WidgetCardContent
        headerIcon={Icons.CUSTOMERVALUE}
        headerText={t(scoShareOfReceiptsTranslations.title.key, scoShareOfReceiptsTranslations.title.defaultValue)}
        subHeaderText={
          t(scoShareOfReceiptsTranslations.subTitle.key, scoShareOfReceiptsTranslations.subTitle.defaultValue) +
          ' ' +
          currentStoreId
        }
        isLoading={performanceItemsLoading}
        toolTipText={t(
          scoShareOfReceiptsTranslations.toolTip.key,
          scoShareOfReceiptsTranslations.toolTip.defaultValue,
        )}
        isError={performanceItemsError}
        errorText={t(scoShareOfReceiptsTranslations.error.key, scoShareOfReceiptsTranslations.error.defaultValue)}
        errorRetryHandler={fetchWidgetData}
        isNoData={isNoData || false}
        target={
          target
            ? `${target.toFixed(2)}%`
            : t(kpiTargetTranslations.notSet.key, kpiTargetTranslations.notSet.defaultValue)
        }
        noDataText={t(commonTranslations.noSCOReceipts.key, commonTranslations.noSCOReceipts.defaultValue)}
        lastUpdatedDate={lastUpdated}
        headerSideContent={
          <DropdownMenu
            data-testid="kpiDropdown"
            dropdownLabel={''}
            buttonContentProps={{
              iconOptions: { customIcon: <Icon icon={Icons.MORE} /> },
            }}
            menuItems={menuItems}
            minWidth={'0px'}
            isCompact
            initialSelected={[]}
            onSelect={(selectedItem) => {
              if (!isEditKPIDisabled) {
                handleDropdownDetails('dailySCOShareOfReceipts', selectedItem[0] as KpiDropdownActions);
              }
            }}
            isOnlyIcon
            allowTooltip
          />
        }
        content={
          <>
            {!isMobile && (
              <Container padding={[2]} wrap="nowrap" data-testid="sco-share-of-receipts-wrapper" height="100%">
                <StyledView>
                  <WidgetMetricTilesContainer>
                    {showForecast && (
                      <WidgetMetricTile
                        metricProperties={{
                          type: WidgetMetricType.ForecastMetric,
                          properties: {
                            metricValue: `${forecastShareOfReceipts}%`,
                            forecastIndicator: getDefaultStatus(targetProgressPercent),
                            metricTooltipText: t(
                              homeTranslations.common.forecastTileTooltip.key,
                              homeTranslations.common.forecastTileTooltip.defaultValue,
                            ),
                          },
                        }}
                      />
                    )}

                    <WidgetMetricTile
                      metricProperties={{
                        type: WidgetMetricType.LegendMetric,
                        properties: {
                          metricTitle: t(
                            homeTranslations.scoShareOfReceipts.totalShareOfReceipts.key,
                            homeTranslations.scoShareOfReceipts.totalShareOfReceipts.defaultValue,
                          ),
                          metricValue: `${currentShareOfReceipts}%`,
                          legendColor: 'validationGreen',
                          metricTooltipText: t(
                            homeTranslations.scoShareOfReceipts.metricTileTooltip.totalShareOfReceiptsTile.key,
                            homeTranslations.scoShareOfReceipts.metricTileTooltip.totalShareOfReceiptsTile
                              .defaultValue,
                          ),
                        },
                      }}
                    />

                    {target !== null && (
                      <WidgetMetricTile
                        isLoading={isKpiLoading}
                        isError={isKpiError}
                        metricProperties={{
                          type: WidgetMetricType.LegendMetric,
                          properties: {
                            metricTitle: t(
                              kpiTargetTranslations.kpiProperties.target.title.key,
                              kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                            ),
                            metricValue: `${target.toFixed(2)}%`,
                            legendColor: 'tagBlue',
                            metricTooltipText: t(
                              homeTranslations.scoShareOfReceipts.metricTileTooltip.targetTile.key,
                              homeTranslations.scoShareOfReceipts.metricTileTooltip.targetTile.defaultValue,
                            ),
                          },
                        }}
                      />
                    )}

                    <WidgetMetricTile
                      metricProperties={{
                        type: WidgetMetricType.RegularMetric,
                        properties: {
                          metricTitle: t(
                            homeTranslations.scoShareOfReceipts.totalSCOTransactions.key,
                            homeTranslations.scoShareOfReceipts.totalSCOTransactions.defaultValue,
                          ),
                          metricValue: `${currentTotalSCOTransactions}`,
                          metricTooltipText: t(
                            homeTranslations.scoShareOfReceipts.metricTileTooltip.totalSCOTransactionsTile.key,
                            homeTranslations.scoShareOfReceipts.metricTileTooltip.totalSCOTransactionsTile
                              .defaultValue,
                          ),
                        },
                      }}
                    />
                  </WidgetMetricTilesContainer>

                  <Container wrap="nowrap" data-testid="chart" style={{ flex: 1, overflow: 'hidden' }}>
                    <WidgetChart
                      performanceItemsChartName={t(
                        homeTranslations.scoShareOfReceipts.sor.key,
                        homeTranslations.scoShareOfReceipts.sor.defaultValue,
                      )}
                      performanceItems={performanceItems}
                      filteredPerformanceItems={filteredPerformanceItems}
                      forecastPerformanceItems={forecastPerformanceItems}
                      targetItems={targetItems}
                      interval={interval}
                      itemValueResolver={itemValueResolver}
                      showXAxis={true}
                      yAxisFormatter={(value) => `${value}%`}
                      tooltipFormatter={(value) => `${formatAmountWithSeparator(value, '%')}`}
                    />
                  </Container>
                </StyledView>
              </Container>
            )}

            {isMobile && (
              <Container padding={[2, 6, 2]} wrap="nowrap" data-testid="chart" style={{ flex: 1, minHeight: 0 }}>
                {showForecast && (
                  <WidgetMetricTile
                    metricProperties={{
                      type: WidgetMetricType.ForecastMetric,
                      properties: {
                        metricValue: `${forecastShareOfReceipts}%`,
                        forecastIndicator: getDefaultStatus(targetProgressPercent),
                        metricTooltipText: t(
                          homeTranslations.common.forecastTileTooltip.key,
                          homeTranslations.common.forecastTileTooltip.defaultValue,
                        ),
                      },
                    }}
                  />
                )}

                <WidgetMobileLayout
                  metricItems={[
                    {
                      label: t(
                        homeTranslations.scoShareOfReceipts.totalShareOfReceipts.key,
                        homeTranslations.scoShareOfReceipts.totalShareOfReceipts.defaultValue,
                      ),
                      content: `${currentShareOfReceipts}%`,
                      tooltipText: t(
                        homeTranslations.scoShareOfReceipts.metricTileTooltip.totalShareOfReceiptsTile.key,
                        homeTranslations.scoShareOfReceipts.metricTileTooltip.totalShareOfReceiptsTile
                          .defaultValue,
                      ),
                    },
                    {
                      label: t(
                        kpiTargetTranslations.kpiProperties.target.title.key,
                        kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                      ),
                      content: target === null ? `-` : `${target.toFixed(2)}%`,
                      isLoading: isKpiLoading,
                      isError: isKpiError,
                      tooltipText: t(
                        homeTranslations.scoShareOfReceipts.metricTileTooltip.targetTile.key,
                        homeTranslations.scoShareOfReceipts.metricTileTooltip.targetTile.defaultValue,
                      ),
                    },

                    {
                      label: t(
                        homeTranslations.scoShareOfReceipts.totalSCOTransactions.key,
                        homeTranslations.scoShareOfReceipts.totalSCOTransactions.defaultValue,
                      ),
                      content: `${currentTotalSCOTransactions}`,
                      tooltipText: t(
                        homeTranslations.scoShareOfReceipts.metricTileTooltip.totalSCOTransactionsTile.key,
                        homeTranslations.scoShareOfReceipts.metricTileTooltip.totalSCOTransactionsTile
                          .defaultValue,
                      ),
                    },
                  ]}
                  renderChart={() => {
                    return (
                      <WidgetChart
                        performanceItemsChartName={t(
                          homeTranslations.shareOfReceipts.sor.key,
                          homeTranslations.shareOfReceipts.sor.defaultValue,
                        )}
                        performanceItems={performanceItems}
                        filteredPerformanceItems={filteredPerformanceItems}
                        forecastPerformanceItems={forecastPerformanceItems}
                        targetItems={targetItems}
                        interval={interval}
                        itemValueResolver={itemValueResolver}
                        showXAxis={true}
                        showXAxisTooltip
                        yAxisFormatter={(value) => `${value}%`}
                        tooltipFormatter={(value) => `${formatAmountWithSeparator(value)}`}
                        sparklineEnabled
                        customYAxis={{ show: false }}
                      />
                    );
                  }}
                />
              </Container>
            )}
          </>
        }
      />
    );
  },
);
